import React, { useCallback, useMemo } from 'react';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { JiraIcon } from '@atlaskit/logo';
import { Box, Grid, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	STATUS_FIELDTYPE,
	type Field,
	CATEGORY_FIELDTYPE,
	EPIC_FIELDTYPE,
} from '../../../common/constants/fields';
import { SUBTASK, TASK } from '../../../common/constants/hierarchy';
import type { SupportedThirdParty } from '../../../common/constants/import-type';
import { renderIcon } from '../../../common/ui/icon';
import { InfoTooltip } from '../../../common/ui/info-tooltip';
import { useContentForThirdParty } from '../../../common/utils';
import { getHierarchyMappingItems } from '../../../common/utils/third-party-mapping';
import { useThirdPartyHierarchyState } from '../../../controllers/import-state/selectors';
import { useUIState } from '../../../controllers/ui-state';
import { useImportTool } from '../../../controllers/utils';
import { ItemSelector } from './item-selector';
import messages from './messages';

export const useTableHead = (thirdParty: SupportedThirdParty) => {
	const { formatMessage } = useIntl();
	const tool = useImportTool(thirdParty);

	return (
		<Grid
			templateColumns={templateColumns}
			alignItems="center"
			xcss={tableHeaderStyles}
			testId="project-create-importer-upload-flow.ui.third-party-review-screen.project-hierarchy.table-header"
			role="row"
		>
			<Box xcss={columnStyles} role="columnheader">
				<Inline space="space.050" alignBlock="center">
					{tool && renderIcon(tool?.icon, true)}
					{tool?.name}
				</Inline>
			</Box>
			<Box as="span" />
			<Inline space="space.050" alignBlock="center" role="columnheader">
				<JiraIcon size="small" appearance="brand" />
				{formatMessage(messages.jwmSpork)}
			</Inline>
			<Box as="span" padding="space.050" />
		</Grid>
	);
};

export const useTableRows = (thirdParty: SupportedThirdParty) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [thirdPartyHierarchyState, { setThirdPartyFieldMapping }] = useThirdPartyHierarchyState({
		types: getHierarchyMappingItems(thirdParty),
		formatMessage,
	});
	const [, { setUIState }] = useUIState();
	const {
		hierarchyStatusToolTip,
		hierarchyCategoryToolTip,
		hierarchyEpicToolTip,
		hierarchySubtaskToolTip,
		disabledFieldTooltip,
	} = useContentForThirdParty(thirdParty);

	const handleOnClick = useCallback(
		(id: string | null | undefined) => (selected: Field) => {
			if (id) {
				setThirdPartyFieldMapping(id, selected);
				setUIState({ hasUnsavedHierarchyMappingChanges: true });

				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'projectHierarchySelector',
				});

				fireUIAnalytics(analyticsEvent, 'projectHierarchyItem changed', {
					selectedHierarchyItem: selected.fieldName,
				});
			}
		},
		[createAnalyticsEvent, setThirdPartyFieldMapping, setUIState],
	);

	const tableRows = useMemo(
		() =>
			thirdPartyHierarchyState?.data.map((field) => {
				const indentType = getIndentType(field.mappedItem.fieldType);
				const tooltipContent = getToolTipForHierarchyItem(field.mappedItem.fieldType, {
					hierarchyStatusToolTip,
					hierarchyCategoryToolTip,
					hierarchyEpicToolTip,
					hierarchySubtaskToolTip,
				});
				return (
					<Grid
						role="row"
						key={field.item.fieldId}
						templateColumns={templateColumns}
						alignItems="center"
					>
						<Box xcss={columnStyles} role="cell">
							{field.item.fieldName}
						</Box>
						<Box as="span" role="cell">
							<ArrowRightIcon size="small" label="Arrow Right" />
						</Box>
						<Box role="cell">
							<ItemSelector
								{...field}
								onClick={handleOnClick(field.item.fieldId)}
								disabledFieldTooltip={disabledFieldTooltip}
								indentType={indentType}
							/>
						</Box>
						{field.showTooltip && (
							<Box as="span" xcss={toolTipColumnStyles} role="cell">
								<InfoTooltip tooltipContent={tooltipContent || ''} position="bottom-start" />
							</Box>
						)}
					</Grid>
				);
			}),
		[
			disabledFieldTooltip,
			handleOnClick,
			hierarchyCategoryToolTip,
			hierarchyEpicToolTip,
			hierarchyStatusToolTip,
			hierarchySubtaskToolTip,
			thirdPartyHierarchyState?.data,
		],
	);

	return tableRows;
};

const templateColumns = '1fr 5% 280px 40px';

const columnStyles = xcss({
	marginLeft: 'space.100',
	textTransform: 'capitalize',
});

const tableHeaderStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `2px solid ${token('color.border', colors.N30)}`,
	paddingBottom: 'space.100',
});

const toolTipColumnStyles = xcss({
	marginLeft: 'space.100',
});

type FieldIndentType = 'large' | 'small' | 'default';
const getIndentType = (fieldType: string): FieldIndentType => {
	switch (fieldType) {
		case TASK:
			return 'small';
		case SUBTASK:
			return 'large';
		default:
			return 'default';
	}
};

const getToolTipForHierarchyItem = (
	fieldType: string,
	data: {
		hierarchyStatusToolTip: string | undefined;
		hierarchyCategoryToolTip: string | undefined;
		hierarchyEpicToolTip: string | undefined;
		hierarchySubtaskToolTip: string | undefined;
	},
) => {
	switch (fieldType) {
		case STATUS_FIELDTYPE:
			return data.hierarchyStatusToolTip;
		case CATEGORY_FIELDTYPE:
			return data.hierarchyCategoryToolTip;
		case EPIC_FIELDTYPE:
			return data.hierarchyEpicToolTip;
		case SUBTASK:
			return data.hierarchySubtaskToolTip;
		default:
			return '';
	}
};
